/**
 * Ledger statement (A+): one account's movements over a period.
 *
 * Four figures lead -- opening, debits, credits, closing -- so the answer
 * is read before the table. Every balance carries Dr or Cr instead of a
 * minus sign: "92,000.00 Cr" is how an accountant reads a credit balance,
 * and "-92,000.00" looks like an error. Each row shows its Hijri date
 * beneath the Gregorian one, and a click on any row (or its voucher
 * number) opens the voucher.
 */
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box, ButtonBase, Skeleton, Stack, Typography, useMediaQuery, useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { fetchLedgerStatement } from '../api/endpoints'
import { useLedgers } from '../store/LedgerCache'
import { useBi } from '../components/Bi'
import LedgerPicker from '../vouchers/LedgerPicker'
import { sidedText } from '../vouchers/money'
import ReportShell, { rangeLabel } from './ReportShell'
import ReportFooter from './ReportFooter'
import { Figure, FigureGrid } from './SummaryFigures'
import ReportToolbar from './ReportToolbar'
import VoucherDrawer from './VoucherDrawer'
import { useDateRange } from './useDateRange'
import { downloadCsv } from './csv'
import { gregorian, toHijri } from './hijri'
import { c, fmtMoney } from '../theme'

interface Props { code?: string; onCodeChange?: (code: string) => void }

const sided = sidedText

export default function LedgerStatement({ code: codeProp, onCodeChange }: Props) {
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const lang = i18n.language
  const { range, setRange, presets } = useDateRange()
  const { byCode } = useLedgers()
  const [code, setCode] = useState(codeProp ?? '')
  const [open, setOpen] = useState<number | null>(null)

  useEffect(() => { if (codeProp) setCode(codeProp) }, [codeProp])

  const q = useQuery({
    enabled: !!code,
    queryKey: ['ledger', code, range.from, range.to],
    queryFn: () => fetchLedgerStatement(code, {
      from_date: range.from, to_date: range.to }),
  })
  const st = q.data
  const led = code ? byCode(code) : undefined
  const name = (lang === 'ar' && led?.name_ar) || led?.name_en || st?.name_en
  const theme = useTheme()
  // A six-column table cannot be read at phone width: list the entries instead.
  const narrow = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <>
      <ReportShell titleKey="nav.ledger"
        period={st ? `${name} (${st.code})  ·  ${rangeLabel(range.from, range.to, lang, t)}`
                   : undefined}
        toolbar={
          <>
            <Box sx={{ px: 2.5, pt: 1.5, maxWidth: 460 }}>
              <Typography component="label" htmlFor="ledger-account"
                sx={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.inkSecondary,
                      mb: 0.75 }}>
                {bi('report.account')}
              </Typography>
              <Box id="ledger-account" sx={{
                border: `1px solid ${c.borderStrong}`, borderRadius: '6px', px: 0.5,
                bgcolor: c.surface,
                '&:focus-within': { borderColor: c.primaryInk,
                                    boxShadow: '0 0 0 3px var(--focus-halo)' } }}>
                <LedgerPicker value={code} placeholder={t('report.chooseAccount')}
                  onChange={(cd) => { setCode(cd); onCodeChange?.(cd) }} />
              </Box>
            </Box>
            <ReportToolbar range={range} setRange={setRange} presets={presets}
              pdf={code ? { path: `ledger/${encodeURIComponent(code)}/pdf`,
                            params: { from_date: range.from, to_date: range.to } }
                        : undefined}
              onExport={st ? () => downloadCsv(`ledger-${code}`, [
                ['Date', 'Voucher', 'Particulars', 'Debit', 'Credit', 'Balance'],
                ['', '', 'Opening', '', '', st.opening],
                ...st.rows.map((r) => [r.date, r.voucher_no, r.particulars,
                                       r.debit, r.credit, r.running]),
                ['', '', 'Closing', st.total_debit, st.total_credit, st.closing],
              ]) : undefined} />
          </>
        }>
        {!code ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            {t('report.pickAccountFirst')}
          </Typography>
        ) : q.isLoading ? (
          <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
        ) : st ? (
          <>
            <FigureGrid>
              <Figure label={bi('report.opening')} value={sided(st.opening)} />
              <Figure label={bi('entry.debit')} value={fmtMoney(st.total_debit)} />
              <Figure label={bi('entry.credit')} value={fmtMoney(st.total_credit)} />
              <Figure label={bi('report.closing')} value={sided(st.closing)} strong />
            </FigureGrid>

            {narrow ? (
              <Box className="ruled" sx={{ borderTop: `1px solid ${c.border}` }}>
                <Stack direction="row" justifyContent="space-between"
                  sx={{ px: 2, py: 1.25, fontSize: 13 }}>
                  <strong>{t('report.opening')}</strong>
                  <span className="num">{sided(st.opening)}</span>
                </Stack>
                {st.rows.map((r, i) => {
                  const dr = Number(r.debit) !== 0
                  return (
                    <Box key={`${r.voucher_id}-${i}`} role="button" tabIndex={0}
                      onClick={() => setOpen(r.voucher_id)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setOpen(r.voucher_id) }}
                      sx={{ px: 2, py: 1.25, cursor: 'pointer' }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1.5}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 500, minWidth: 0 }} noWrap>
                          {r.particulars}</Typography>
                        <span className="num" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {fmtMoney(dr ? r.debit : r.credit)} {dr ? 'Dr' : 'Cr'}
                        </span>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" spacing={1.5}
                        sx={{ fontSize: 12, color: c.inkMuted, mt: 0.25 }}>
                        <span>
                          {gregorian(r.date, lang)} · <span className="doc-no"
                            style={{ color: 'var(--primary-ink)', fontWeight: 400 }}>
                            {r.voucher_no}</span>
                        </span>
                        <span className="num" style={{ whiteSpace: 'nowrap' }}>
                          {t('report.balance')} {sided(r.running)}</span>
                      </Stack>
                    </Box>
                  )
                })}
                <Stack direction="row" justifyContent="space-between"
                  sx={{ px: 2, py: 1.5, borderTop: `2px solid ${c.ink}`, fontWeight: 700 }}>
                  <span>{t('report.closing')}</span>
                  <span className="num">{sided(st.closing)}</span>
                </Stack>
              </Box>
            ) : (
            <Box sx={{ px: 2.5, pb: 1, overflowX: 'auto' }}>
              <table className="rpt" style={{ minWidth: 720 }}>
                <thead>
                  <tr>
                    <th style={{ width: 136 }}>{t('entry.date')}</th>
                    <th style={{ width: 150 }}>{t('report.voucherNo')}</th>
                    <th>{bi('entry.particulars')}</th>
                    <th className="num" style={{ width: 120 }}>{bi('entry.debit')}</th>
                    <th className="num" style={{ width: 120 }}>{bi('entry.credit')}</th>
                    <th className="num" style={{ width: 150 }}>{bi('report.balance')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="no-hover">
                    <td>{gregorian(st.from_date, lang)}</td>
                    <td />
                    <td style={{ fontWeight: 600 }}>{t('report.opening')}</td>
                    <td /><td />
                    <td className="num" style={{ fontWeight: 600 }}>{sided(st.opening)}</td>
                  </tr>
                  {st.rows.map((r, i) => (
                    <tr key={`${r.voucher_id}-${i}`} className="clickable"
                      onClick={() => setOpen(r.voucher_id)}>
                      <td>
                        <div>{gregorian(r.date, lang)}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--ink-muted)',
                                      whiteSpace: 'nowrap' }}>
                          {toHijri(r.date, lang)}</div>
                      </td>
                      <td>
                        <ButtonBase className="doc-no"
                          onClick={(e) => { e.stopPropagation(); setOpen(r.voucher_id) }}
                          sx={{ color: c.primaryInk, fontSize: 13, borderRadius: '4px',
                                '&:hover': { textDecoration: 'underline' },
                                '&:focus-visible': { outline: '2px solid var(--focus-ring)' } }}>
                          {r.voucher_no}
                        </ButtonBase>
                        {r.voucher_kind && (
                          <div style={{ fontSize: 11.5, color: 'var(--ink-muted)' }}>
                            {t(`voucher.${r.voucher_kind.toLowerCase()}`,
                               { defaultValue: r.voucher_kind })}
                          </div>
                        )}
                      </td>
                      <td>
                        <div>{r.particulars}</div>
                        {r.narration && (
                          <div style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
                            {r.narration}</div>
                        )}
                      </td>
                      <td className="num">{Number(r.debit) ? fmtMoney(r.debit) : ''}</td>
                      <td className="num">{Number(r.credit) ? fmtMoney(r.credit) : ''}</td>
                      <td className="num" style={{ color: 'var(--ink-secondary)' }}>
                        {sided(r.running)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="total-row">
                    <td colSpan={3}>{t('report.closing')}</td>
                    <td className="num">{fmtMoney(st.total_debit)}</td>
                    <td className="num">{fmtMoney(st.total_credit)}</td>
                    <td className="num" style={{ color: 'var(--primary-ink)' }}>
                      {sided(st.closing)}</td>
                  </tr>
                </tfoot>
              </table>
              {st.rows.length > 0 && (
                <Typography className="no-print"
                  sx={{ fontSize: 12, color: c.inkMuted, pt: 1.25 }}>
                  {t('report.rowHint')}
                </Typography>
              )}
            </Box>
            )}
          </>
        ) : null}
        <ReportFooter />
      </ReportShell>

      <VoucherDrawer voucherId={open} onClose={() => setOpen(null)} />
    </>
  )
}
